import { inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class CommentVisibilityService {
  private readonly document = inject(DOCUMENT);

  isCommentAllowed(url: string): boolean {
    const pathname = this.resolvePathname(url);
    if (!pathname) {
      return false;
    }

    if (this.isCompanyEditPath(pathname)) {
      return false;
    }

    if (this.isNotificationSettingPath(pathname)) {
      return false;
    }

    if (this.isCampaignDriveGeneralPath(pathname)) {
      return false;
    }

    if (this.isCampaignDriveTimelineSettingPath(pathname)) {
      return false;
    }

    if (this.isCampaignDriveRecapPath(pathname)) {
      return false;
    }

    if (this.isNewCampaignDriveRecapPath(pathname)) {
      return false;
    }

    if (this.isCampaignDriveRootGeneralPath(pathname)) {
      return false;
    }

    if (this.isProjectNewCompanyPath(pathname)) {
      return false;
    }

    if (this.isTasksPath(pathname)) {
      return false;
    }

    if (pathname === '/projects' || pathname.startsWith('/projects/')) {
      return true;
    }

    if (pathname === '/my-space' || pathname.startsWith('/my-space/')) {
      return true;
    }

    if (pathname === '/share' || pathname.startsWith('/share/')) {
      return true;
    }

    if (pathname === '/workspaces' || pathname === '/workspaces/') {
      return true;
    }

    if (pathname === '/workspaces/projects' || pathname.startsWith('/workspaces/projects/')) {
      return false;
    }

    return pathname.startsWith('/workspaces/');
  }

  private resolvePathname(url: string): string {
    if (!url) {
      return '';
    }

    const baseOrigin = this.document?.location?.origin ?? '';
    const normalizedUrl = url.startsWith('http') ? url : `${baseOrigin}${url}`;

    try {
      return new URL(normalizedUrl).pathname;
    } catch {
      return url.split('?')[0] ?? '';
    }
  }

  private isCompanyEditPath(pathname: string): boolean {
    return /^\/projects\/edit\/[^/]+\/company\/?$/.test(pathname);
  }

  private isNotificationSettingPath(pathname: string): boolean {
    return /^\/projects\/[^/]+\/notification-setting\/?$/.test(pathname);
  }

  private isCampaignDriveGeneralPath(pathname: string): boolean {
    return /^\/workspaces\/campaigndrive\/[^/]+\/general\/?$/.test(pathname);
  }

  private isCampaignDriveRecapPath(pathname: string): boolean {
    return /^\/workspaces\/campaigndrive\/[^/]+\/recap\/?$/.test(pathname);
  }

  private isCampaignDriveTimelineSettingPath(pathname: string): boolean {
    return /^\/workspaces\/campaigndrive\/timeline-setting\/?$/.test(pathname);
  }

  private isNewCampaignDriveRecapPath(pathname: string): boolean {
    return /^\/workspaces\/campaigndrive\/[^/]+\/recap\/?$/.test(pathname);
  }

  private isCampaignDriveRootGeneralPath(pathname: string): boolean {
    return /^\/workspaces\/campaigndrive\/general\/?$/.test(pathname);
  }

  private isProjectNewCompanyPath(pathname: string): boolean {
    return /^\/projects\/new\/company\/?$/.test(pathname);
  }

  private isTasksPath(pathname: string): boolean {
    return /\/tasks(\/|$)/.test(pathname);
  }
}
